/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { Box, Grid, Link } from '@mui/material';

import { LanguagePicker } from '../../components/LanguagePicker/LanguagePicker';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { LoginTagLine } from './components/LoginTagLine/LoginTagLine';
import { useComponentStyles } from './LoginLayout.styles';

type IProps = {
  children: any;
};

export const LoginLayout = memo<IProps>(props => {
  const { children } = props;
  const location = useLocation();
  const navigate = useNavigate();

  const classes = useComponentStyles();
  const { t } = useTranslation();

  const handleChange = (newRoute: string) => {
    switch (location.pathname) {
      case BasicRoutes.LOGIN:
        void navigate(newRoute);
        break;
      case BasicRoutes.SIGN_UP:
        void navigate(newRoute);
        break;
      default:
        break;
    }
  };

  return (
    <Grid container component="main" className={classes.root}>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <Grid className={classes.loginItem}>
          <div className={classes.loginSignup}>
            {location.pathname === BasicRoutes.LOGIN && (
              <Grid container direction="row">
                <Box sx={{ display: { xs: 'none', sm: 'block' } }} className={classes.loginHeadingSecondary}>
                  {t(`${Namespaces.Login}:page.login.not_registered`)}
                </Box>
                <Link
                  component="button"
                  onClick={() => handleChange(BasicRoutes.SIGN_UP)}
                  className={classes.loginHeadingLink}
                  data-cy="landing-page-goto-sign-up"
                >
                  {t(`${Namespaces.Login}:page.login.sign_up`)}
                </Link>
              </Grid>
            )}
            {location.pathname === BasicRoutes.SIGN_UP && (
              <Grid container direction="row">
                <Box sx={{ display: { xs: 'none', sm: 'block' } }} className={classes.loginHeadingSecondary}>
                  {t(`${Namespaces.Login}:page.login.already_registered`)}
                </Box>
                <Link
                  component="button"
                  onClick={() => handleChange(BasicRoutes.LOGIN)}
                  className={classes.loginHeadingLink}
                >
                  {t(`${Namespaces.Login}:page.login.title`)}
                </Link>
              </Grid>
            )}
            <LanguagePicker />
          </div>
        </Grid>
      </Grid>

      <Grid container className={classes.loginBody}>
        <Box
          component={Grid}
          sx={{
            display: {
              xs: 'none',
              sm: 'none',
              md: 'none',
              lg: 'block',
              xl: 'block',
            },
          }}
          className={classes.divider}
          item
          lg={6}
          xl={6}
        >
          <LoginTagLine />
        </Box>

        <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
          <div className={classes.loginWrapper}>
            <div className={classes.loginContainer}>{children}</div>
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
});
